// dependencies
import { useDispatch } from "react-redux";
import { useReducer, useRef } from "react";
import { GenerateProcess } from "redux-process";
import useDeepCompareEffect from "use-deep-compare-effect";

// components

// utils
import get from "Utils/get";
import uuid from "Utils/uuid";

const reverseGet = (nestedObj, path, notSetValue = undefined) => get(path, nestedObj, (notSetValue = undefined));

/**
 * Handles different states of the request lifecycle.
 *
 * @param {String} processName - name of the redux process
 * @param {Object} query - query object
 */
const useRequest = (processName, query = {}, options = {}) => {
	const dispatch = useDispatch();
	const mostRecentRequestID = useRef(null);

	// if manual hook wont run unless explicitly told to via revalidate function
	const manual = reverseGet(options, ["manual"], false);

	// if ignoreFailed, warning pop up will be ignored for 404 errors
	const ignoreFailed = reverseGet(options, ["ignoreFailed"], false);

	// cache options
	const timeToLive = reverseGet(options, ["timeToLive"], 1000);

	const [state, setState] = useReducer((currentState, nextState) => ({ ...currentState, ...nextState }), {
		data: null,
		error: null,
		isRequesting: false,
		query: {},
	});

	const sendRequest = async (requestData = query) => {
		const requestID = uuid();
		mostRecentRequestID.current = requestID;

		// update requesting state
		setState({ isRequesting: true });

		// create a reference to the query that will be used in the request
		let mutatedQuery = requestData;

		// if options has an `onBeforeRequest` hook, run it. This can be used
		// to modify filters
		if (options.onBeforeRequest) {
			mutatedQuery = options.onBeforeRequest(mutatedQuery);
		}

		const request = await dispatch(
			GenerateProcess(processName, {
				params: {
					...mutatedQuery.filters,
					...(reverseGet(mutatedQuery, ["skip"], null) !== null ? { skip: mutatedQuery.skip } : {}),
					...(reverseGet(mutatedQuery, ["limit"], null) !== null ? { limit: mutatedQuery.limit } : {}),
					...(reverseGet(mutatedQuery, ["sort", "name"], null) ? { sortName: mutatedQuery.sort.name } : {}),
					...(reverseGet(mutatedQuery, ["sort", "direction"], null)
						? { sortDirection: mutatedQuery.sort.direction }
						: {}),
					...(ignoreFailed ? { ignoreFailed: true } : {}),
				},
			})
		);

		// helper variable used to handle prevent conditions
		const isMostRecentRequest = mostRecentRequestID.current === requestID;

		// if the request succeeded and it is the most recent request, return
		// a valid response
		const responseData = request.succeeded && isMostRecentRequest ? request.response : null;

		// if current request is the most recent, update the request state and
		// data
		if (isMostRecentRequest) {
			// update the state to no longer be requesting, set data
			setState({ isRequesting: false, data: responseData });
		}

		return {
			data: responseData,
		};
	};

	const revalidate = async (requestData) => sendRequest(requestData);

	useDeepCompareEffect(() => {
		if (!manual) {
			sendRequest();
		}
	}, [processName, query]);

	return {
		revalidate,
		data: state.data,
		error: state.error,
		isRequesting: state.isRequesting,
	};
};

export default useRequest;
